import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useListUnsignedChecklistRunsQuery,
  useSignOffChecklistRunsMutation,
} from '../../store/api/localApi';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useToast } from '../../components/Toast';
import { ChecklistKind, ClientChecklistRun } from '../../types';
import { ChecklistRunDetails } from '../../components/checklists/ChecklistRunDetails';

const KIND_LABELS: Record<ChecklistKind, string> = {
  [ChecklistKind.OPENING]: 'Opening',
  [ChecklistKind.CLOSING]: 'Closing',
};

function unansweredRequiredCount(run: ClientChecklistRun): number {
  const answered = new Set(run.answers.map((a) => a.itemId));
  return run.items.filter((i) => i.isRequired && !answered.has(i.itemId)).length;
}

/**
 * Admin-only: every unsigned run at this location, newest first, with a
 * batch "Accept selected" that signs every checked run in one request.
 *
 * ADMIN-ONLY — gated on the same `isAdmin` signal the sibling compliance
 * pages (Cleaning, Policy Vault) use. Not a statutory record: no Tusla/HSA
 * claim, and an incomplete run is shown neutrally (no red/alarm styling) —
 * it just can't be selected, because the server would skip it anyway.
 */
const ChecklistSignOffQueue: React.FC = () => {
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId } = useLocationContext();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;

  const { isAdmin } = useAuth();
  const { showError } = useToast();

  // Scoped to this location — the sparse-GSI-backed queue, no lookback
  // window: a run awaiting sign-off stays in view no matter its age.
  const { data, isLoading, isError, refetch } = useListUnsignedChecklistRunsQuery(
    { locationId: locationId ?? '' },
    { skip: !locationId || !isAdmin }
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Which runs are expanded to show their answers and note. The manager reads
  // what they are signing; collapsed by default so the queue stays scannable.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [signOffRuns, { isLoading: isSubmitting }] = useSignOffChecklistRunsMutation();
  const [lastResult, setLastResult] = useState<{ signed: number; skipped: number } | null>(null);

  if (!isAdmin) {
    // Staff must not see this page at all — no queue container, no data
    // fetch (the query above is already skipped).
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        You don&apos;t have access to this page.
      </div>
    );
  }

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">Failed to load the sign-off queue.</p>
        <button
          type="button"
          data-testid="checklist-queue-retry"
          onClick={() => refetch()}
          className="text-primary-600 hover:text-primary-500 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const runs = (data?.runs ?? [])
    // The server already scopes this to unsigned runs (GSI2 removes the
    // GSI2PK/GSI2SK attributes on sign-off); this filter is defence-in-depth
    // against a stale cache entry, not the primary filter.
    .filter((r) => r.status !== 'SIGNED_OFF')
    // Newest first — most recent date, then most recently started within a
    // date (a location can have both an OPENING and CLOSING run on the
    // same date).
    .slice()
    .sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? 1 : -1;
      return a.startedAt < b.startedAt ? 1 : -1;
    });

  const toggle = (runId: string, incomplete: boolean) => {
    if (incomplete) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    try {
      const result = await signOffRuns({ runIds: Array.from(selected) }).unwrap();
      setLastResult({ signed: result.signed, skipped: result.skipped });
      setSelected(new Set());
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to sign off the selected runs');
    }
  };

  return (
    // Rendered inside the Checklists tabbed wrapper (admin-only), which supplies
    // the page frame and the "Sign-off" tab title, so no outer padding or h1
    // here — just the descriptive line and the queue.
    <div data-testid="checklist-signoff-queue" className="space-y-6 py-6">
      <p className="text-sm text-gray-600">Every run awaiting sign-off at this location.</p>

      {lastResult && (
        <p data-testid="checklist-signoff-result" className="text-sm text-gray-600">
          Signed {lastResult.signed}, skipped {lastResult.skipped}.
        </p>
      )}

      {runs.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">Nothing waiting on sign-off.</p>
      ) : (
        <div className="space-y-2">
          {runs.map((run) => {
            const remaining = unansweredRequiredCount(run);
            const incomplete = remaining > 0;
            const isExpanded = expanded.has(run.runId);
            return (
              <div
                key={run.runId}
                data-testid="checklist-queue-row"
                className="rounded-lg border border-gray-200 bg-white shadow-sm"
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    data-testid={`checklist-queue-select-${run.runId}`}
                    checked={selected.has(run.runId)}
                    disabled={incomplete}
                    onChange={() => toggle(run.runId, incomplete)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      {KIND_LABELS[run.kind]} — {run.date}
                      {run.note && (
                        <span
                          data-testid={`checklist-queue-note-badge-${run.runId}`}
                          className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
                        >
                          Has note
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      Started by {run.startedByName}
                      {incomplete && (
                        <span
                          data-testid="checklist-queue-incomplete"
                          className="ml-2 text-gray-500"
                        >
                          {remaining} required item{remaining === 1 ? '' : 's'} still unanswered
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid={`checklist-signoff-run-toggle-${run.runId}`}
                    aria-expanded={isExpanded}
                    onClick={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        if (next.has(run.runId)) next.delete(run.runId);
                        else next.add(run.runId);
                        return next;
                      })
                    }
                    className="shrink-0 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    {isExpanded ? 'Hide details' : 'Show details'}
                  </button>
                </div>
                {isExpanded && <ChecklistRunDetails run={run} />}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          data-testid="checklist-signoff-submit"
          onClick={handleSubmit}
          disabled={selected.size === 0 || isSubmitting}
          className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting
            ? 'Submitting…'
            : `Accept selected${selected.size > 0 ? ` (${selected.size})` : ''}`}
        </button>
      </div>
    </div>
  );
};

export default ChecklistSignOffQueue;
